import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaRobot, FaChalkboardTeacher, FaFlask } from 'react-icons/fa';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us | STEM Labs</title>
        <meta name="description" content="Learn about STEM Labs, the institute of STEM education and our mission to inspire future innovators through hands-on robotics and STEM education." />
      </Helmet>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            About STEM Labs
          </h1>
          <p className="text-white text-xl max-w-3xl mx-auto text-center">
            The Institute of STEM Education dedicated to developing future innovators, scientists, and entrepreneurs
          </p>
        </div>
      </div>
      
      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-blue-800">Our Mission</h2>
            <p className="text-gray-700 mb-8 text-lg">
              STEM Labs is an innovative educational institution dedicated to improving the Science, Technology, Engineering, and Mathematics (STEM) skills of students. We have specially designed our courses to enhance children's knowledge and technical capabilities through hands-on, project-based learning.
            </p>
            
            <p className="text-gray-700 mb-12 text-lg">
              Our main focus is to nurture talented students who will become the innovators and entrepreneurs of the future, creating pathways to success in the rapidly evolving job market. Through our unique approach of teaching STEM subjects using robotics, we make complex concepts accessible and engaging for learners of all ages.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start">
                  <FaRobot className="text-3xl text-blue-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Hands-on Learning</h3>
                    <p className="text-gray-600">
                      We believe in learning by doing. Our students build, program, and test real robots and electronic systems, developing practical skills that go beyond theoretical knowledge.
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start">
                  <FaChalkboardTeacher className="text-3xl text-green-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Expert Guidance</h3>
                    <p className="text-gray-600">
                      Our qualified instructors provide personalized attention and mentorship, ensuring each student receives the support needed to excel in their STEM journey.
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-600"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start">
                  <FaFlask className="text-3xl text-red-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Innovation-Focused</h3>
                    <p className="text-gray-600">
                      We foster creativity and critical thinking, encouraging students to develop innovative solutions to real-world problems using STEM principles.
                    </p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-600"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start">
                  <FaGraduationCap className="text-3xl text-purple-600 mr-4 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Future-Ready Skills</h3>
                    <p className="text-gray-600">
                      Our curriculum is designed to prepare students for future careers in technology, engineering, and science fields that will shape the world of tomorrow.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
            
            <h2 className="text-3xl font-bold mb-6 text-blue-800">STEM Education for the Coming Future</h2>
            <p className="text-gray-700 mb-8 text-lg">
              Educational Robotics allows students to learn STEM disciplines in innovative ways. By combining the excitement of building and programming robots with fundamental science and math concepts, we create an engaging learning environment that inspires curiosity and fosters a passion for learning.
            </p>
            
            <p className="text-gray-700 mb-12 text-lg">
              Our comprehensive approach includes certificate courses, specialized workshops, competitions, and events designed to challenge students and showcase their achievements. We believe that every child has the potential to become a creator of technology rather than just a consumer.
            </p>
            
            <div className="bg-gray-100 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4 text-center">Join Us on This Learning Journey</h3>
              <p className="text-center mb-6">
                Discover how STEM Labs can help your child develop the skills needed for future success
              </p>
              <div className="flex justify-center">
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfcR5SGMKi9XrdgFROEUukUL5oINfiQQy-cnezcsjt0KbSHOQ/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full hover:bg-blue-700 transition-colors"
                >
                  Apply Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage; 